import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
    InfiniteLoader,
    List,
} from 'react-virtualized';
import {DataRequestType, DataRequestVO, DataResponseVO} from '@mc/data-filter-react';
import {ViewportResult} from './searchPanelTypes';

type Props = {
    className?: string;
    rowClassName?: string;
    ids: string[];
    api: (request: DataRequestVO<object>) => Promise<DataResponseVO<ViewportResult>>;
    defaultRowHeight: number;
    overscanRowCount?: number;
    children: (row: ViewportResult) => React.ReactNode;
    renderLoading: () => React.ReactNode;
};

export const ViewportVirtualList: React.FC<Props> = ({className, rowClassName, ids, api, defaultRowHeight, overscanRowCount = 5, children, renderLoading}) => {
    const cacheRef = useRef(new CellMeasurerCache({fixedWidth: true, defaultHeight: defaultRowHeight}));
    const loadedRef = useRef<{[id: string]: ViewportResult | null}>({});
    const listRef = useRef<List | null>(null);
    const infiniteLoaderRef = useRef<InfiniteLoader | null>(null);
    const [, forceRerender] = useState(0);

    useEffect(() => {
        loadedRef.current = {};
        cacheRef.current.clearAll();
        infiniteLoaderRef.current?.resetLoadMoreRowsCache(true);
        listRef.current?.recomputeRowHeights(0);
        forceRerender(n => n + 1);
    }, [ids]);

    const isRowLoaded = useCallback(({index}: {index: number}) => loadedRef.current[ids[index]] !== undefined, [ids]);

    const loadMoreRows = useCallback(
        async ({startIndex, stopIndex}: {startIndex: number; stopIndex: number}) => {
            const windowIds = ids.slice(startIndex, stopIndex + 1).filter(id => loadedRef.current[id] === undefined);
            if (windowIds.length === 0) return;
            const response = await api({requestType: DataRequestType.DATA, sort: [], idsRequest: {ids: windowIds as unknown as object[]}});
            (response.rows ?? []).forEach(row => {
                loadedRef.current[row.id] = row;
            });
            windowIds.forEach(id => {
                if (loadedRef.current[id] === undefined) loadedRef.current[id] = null;
            });
            for (let i = startIndex; i <= stopIndex; i++) cacheRef.current.clear(i, 0);
            listRef.current?.recomputeRowHeights(startIndex);
            forceRerender(n => n + 1);
        },
        [ids, api],
    );

    const rowRenderer = useCallback(
        ({index, key, parent, style}: any) => {
            const row = loadedRef.current[ids[index]];
            return (
                <CellMeasurer cache={cacheRef.current} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                    {({registerChild}: any) => (
                        <div ref={registerChild} style={style} className={rowClassName}>
                            {row ? children(row) : renderLoading()}
                        </div>
                    )}
                </CellMeasurer>
            );
        },
        [ids, children, renderLoading, rowClassName],
    );

    return (
        <div className={className} style={{height: '100%'}}>
            <InfiniteLoader ref={infiniteLoaderRef} isRowLoaded={isRowLoaded} loadMoreRows={loadMoreRows} rowCount={ids.length} minimumBatchSize={20} threshold={15}>
                {({onRowsRendered, registerChild}: any) => (
                    <AutoSizer>
                        {({width, height}: any) => (
                            <List
                                deferredMeasurementCache={cacheRef.current}
                                ref={(list: List | null) => {
                                    listRef.current = list;
                                    registerChild(list);
                                }}
                                width={width}
                                height={height}
                                rowCount={ids.length}
                                rowHeight={cacheRef.current.rowHeight}
                                rowRenderer={rowRenderer}
                                onRowsRendered={onRowsRendered}
                                overscanRowCount={overscanRowCount}
                            />
                        )}
                    </AutoSizer>
                )}
            </InfiniteLoader>
        </div>
    );
};
